<template>
    <!-- ITDEVELOP-2722: ui/Dialog replaces the scrim-div shell. Always open
         while mounted (consumers v-if this component); Esc / outside-click emit
         cancel via the primitive. The scrollHeight-pinning hack is obsolete —
         DialogContent sizes itself. -->
    <Dialog :open="true" @update:open="(open) => !open && $emit('cancel')">
        <!-- max-w-none must come BEFORE widthClass: tailwind-merge keeps the
             last conflicting utility, so the caller's own max-w cap (e.g.
             max-w-[90vw] on phones) survives while the dialog's built-in size
             cap is still lifted for the default half-viewport width. -->
        <DialogContent name="confirmation-modal" :class="cn('max-w-none', widthClass)" :aria-describedby="undefined">
            <DialogTitle>{{ props.title }}</DialogTitle>
            <div class="gap-sm flex flex-col">
                <p>{{ props.question }}</p>
                <p v-if="props.infoMessage">{{ props.infoMessage }}</p>
                <div class="gap-md flex">
                    <Button variant="destructive" :disabled="props.disabled" @click="confirm">
                        {{ props.buttonText }}
                    </Button>
                    <Button variant="secondary" @click="$emit('cancel')">Cancel</Button>
                </div>
            </div>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

const emit = defineEmits(['cancel', 'confirm']);

const props = defineProps({
    title: {
        type: String,
        required: true,
    },
    question: {
        type: String,
        required: true,
    },
    infoMessage: {
        type: String,
        required: false,
    },
    buttonText: {
        type: String,
        required: true,
    },
    // Tailwind width class(es) for the modal panel. Defaults to the original
    // half-viewport width; pass something narrower (e.g. 'w-[28rem] max-w-[90vw]')
    // for simple single-line confirmations.
    widthClass: {
        type: String,
        required: false,
        default: 'w-1/2',
    },
    // Gates confirm only. Cancel stays available while a confirmation is in flight so
    // the user is never trapped in the modal by a slow or hung request.
    disabled: {
        type: Boolean,
        default: false,
    },
});

// The native disabled attribute already blocks user clicks; the guard keeps the
// contract explicit for programmatic dispatch too.
function confirm() {
    if (!props.disabled) emit('confirm');
}
</script>
